import { memo, useCallback, useMemo } from 'react'
import { FileVideo, Image, Layers, ListVideo, TriangleAlert } from 'lucide-react'
import type { Program } from '../../types/index.js'
import type { CatalogOverview, WorkItemReason } from '../../hooks/useCatalogOverview.js'
import type { WorkBucket } from '../../lib/api.js'
import { PROGRAM_TYPE_LABELS, formatDurationShort } from '../../lib/utils.js'
import { Spinner } from '../common/Spinner.js'

const WORK_LABELS: Record<WorkItemReason, { title: string; help: string }> = {
  'no-media': {
    title: 'Sans média',
    help: 'Aucun fichier associé — ces programmes ne peuvent pas partir à l’antenne.',
  },
  'missing-file': {
    title: 'Fichier absent du disque',
    help: 'Un média est référencé mais introuvable au dernier contrôle.',
  },
  'no-branding': {
    title: 'Sans habillage',
    help: 'Ni logo antenne ni profil d’habillage : la diffusion partira sans bug.',
  },
  'missing-epg': {
    title: 'Infos EPG incomplètes',
    help: 'Titre court ou résumé manquant : la fiche partira au guide des programmes sans ces informations.',
  },
}

/** Nombre de vignettes proposées : un rappel de ce qu'on vient de toucher, pas un catalogue. */
const RECENT_TILE_COUNT = 5

/** Jour et mois de la dernière modification, pour situer la vignette. */
const shortDate = (iso: string | undefined) => {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}

interface ProgramsOverviewProps {
  overview:  CatalogOverview
  programs:  Program[]
  /** Les cartes « À traiter » ne reçoivent qu'un identifiant et un titre. */
  onSelect:  (programId: string) => void
}

/**
 * Écran Programmes sans sélection.
 *
 * Demande explicite du commanditaire : le centre ne doit jamais rester vide.
 * Tout ce qui est affiché ici est dérivé de ce que /programs renvoie déjà —
 * il n'y a pas de compteur inventé, et le volume de stockage n'apparaît pas
 * puisque la taille des fichiers n'est pas dans la réponse de liste.
 */
export const ProgramsOverview = memo(function ProgramsOverview({
  overview, programs, onSelect,
}: ProgramsOverviewProps) {
  const hours = Math.round(overview.totalDurationSec / 3600)

  /**
   * Les derniers programmes touchés, au lieu de la grille complète.
   * Cinquante vignettes ne répondaient à aucune question : elles répétaient la
   * liste de gauche sur plusieurs écrans de défilement. Reprendre son travail
   * là où on l'a laissé, si.
   */
  const recent = useMemo(
    () => [...programs]
      .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
      .slice(0, RECENT_TILE_COUNT),
    [programs],
  )

  return (
    <div className="programs-overview">
      <div className="programs-overview__counters">
        <Counter
          icon={<ListVideo size={15} />}
          label="Programmes"
          value={String(overview.programCount)}
          detail={`${hours} h de catalogue`}
        />
        <Counter
          icon={<Layers size={15} />}
          label="Séries & collections"
          value={`${overview.seriesCount} / ${overview.collectionCount}`}
          detail="séries · collections"
        />
        <Counter
          icon={<FileVideo size={15} />}
          label="Médias"
          value={String(overview.mediaCount)}
          detail={overview.mediaNeedingCheck > 0
            ? `${overview.mediaNeedingCheck} à contrôler`
            : 'tous contrôlés'}
        />
        <Counter
          icon={<TriangleAlert size={15} />}
          label="À traiter"
          value={String(overview.workTotal)}
          detail={overview.workTotal > 0 ? 'programmes concernés' : 'catalogue sain'}
          accent
        />
      </div>

      <section className="programs-overview__work" aria-label="À traiter en priorité">
        <h2 className="programs-overview__heading">À traiter en priorité</h2>
        <div className="programs-overview__work-grid">
          {(Object.keys(WORK_LABELS) as WorkItemReason[]).map(reason => (
            <WorkCard
              key={reason}
              reason={reason}
              bucket={overview.work[reason]}
              onSelect={onSelect}
            />
          ))}
        </div>
      </section>

      <section className="programs-overview__catalog" aria-label="Modifiés récemment">
        <h2 className="programs-overview__heading">
          Modifiés récemment
          {programs.length > 0 && (
            <span className="programs-overview__count">{recent.length} / {programs.length}</span>
          )}
        </h2>
        {recent.length === 0 ? (
          <p className="programs-overview__empty">
            {overview.loading ? <><Spinner /> Chargement du catalogue…</> : 'Aucun programme ne correspond aux filtres.'}
          </p>
        ) : (
          <div className="programs-overview__tiles">
            {recent.map(program => (
              <ProgramTile key={program.id} program={program} onSelect={onSelect} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
})

const Counter = memo(function Counter({
  icon, label, value, detail, accent = false,
}: {
  icon: React.ReactNode
  label: string
  value: string
  detail: string
  accent?: boolean
}) {
  return (
    <div className={`programs-counter${accent ? ' is-accent' : ''}`}>
      <div className="programs-counter__label">
        <span aria-hidden="true">{icon}</span>
        {label}
      </div>
      <div className="programs-counter__value">{value}</div>
      <div className="programs-counter__detail">{detail}</div>
    </div>
  )
})

const WorkCard = memo(function WorkCard({
  reason, bucket, onSelect,
}: {
  reason:   WorkItemReason
  bucket:   WorkBucket
  onSelect: (programId: string) => void
}) {
  const { title, help } = WORK_LABELS[reason]

  return (
    <div className={`programs-work-card${bucket.count > 0 ? ' is-active' : ''}`}>
      <div className="programs-work-card__header">
        <span className="programs-work-card__title">{title}</span>
        <span className="programs-work-card__count">{bucket.count}</span>
      </div>
      <p className="programs-work-card__help">{help}</p>
      {bucket.count === 0 ? (
        <p className="programs-work-card__clear">Rien à traiter.</p>
      ) : (
        <ul className="programs-work-card__list">
          {bucket.sample.map(item => (
            <li key={item.id}>
              <button type="button" onClick={() => onSelect(item.id)} title={`Ouvrir ${item.title}`}>
                {item.title}
              </button>
            </li>
          ))}
          {bucket.count > bucket.sample.length && (
            <li className="programs-work-card__more">+ {bucket.count - bucket.sample.length} autre(s)</li>
          )}
        </ul>
      )}
    </div>
  )
})

const ProgramTile = memo(function ProgramTile({
  program, onSelect,
}: {
  program:  Program
  onSelect: (programId: string) => void
}) {
  const handleClick = useCallback(() => onSelect(program.id), [onSelect, program.id])
  const media = [...(program.mediaAssets ?? []), ...(program.parts ?? []).flatMap(part => part.mediaAssets ?? [])]
  const missing = media.length === 0 && !program.isLive
  const duration = program.totalDurationSec ?? program.durationSec
  const modified = shortDate(program.updatedAt)

  return (
    <button type="button" className="programs-tile" onClick={handleClick} title={program.title}>
      <span className="programs-tile__thumb">
        {program.thumbnailUrl
          ? <img src={program.thumbnailUrl} alt="" loading="lazy" />
          : <Image size={18} aria-hidden="true" />}
        {missing && <span className="programs-tile__flag">SANS MÉDIA</span>}
      </span>
      <span className="programs-tile__title">{program.title}</span>
      <span className="programs-tile__meta">
        {program.programCategory?.label ?? PROGRAM_TYPE_LABELS[program.type] ?? program.type} · {formatDurationShort(duration)}
        {modified && <> · modifié le {modified}</>}
      </span>
    </button>
  )
})
